import { Injectable, computed, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter } from 'rxjs';

import { SessionService } from './session.service';

/** What the user picked; 'system' follows the OS setting. */
export type AppTheme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'mila_app_theme';
const PREFERENCE_KEY = 'theme';

/** Which side of --color-* (_base.scss) resolves dark or light, for the
 *  app outside /docs. Docs keeps its own setting (DocsThemeService,
 *  --docs-*), so `data-theme` is dropped from <html> while a /docs route
 *  is showing and the docs pages always see the light --color-* values.
 *
 *  Saved per user in user.preferences.theme (PATCH /me/preferences); a
 *  missing key means 'system'. localStorage keeps a copy so the first
 *  paint, before GET /me answers, already has the right theme, and so
 *  signed-out pages remember it. */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _theme = signal<AppTheme>(this.readStored() ?? 'system');
  readonly theme = this._theme.asReadonly();

  private readonly systemQuery = matchMedia('(prefers-color-scheme: dark)');
  private readonly systemDark = signal(this.systemQuery.matches);

  readonly isDark = computed(() => {
    const theme = this._theme();
    return theme === 'system' ? this.systemDark() : theme === 'dark';
  });

  private onDocs = false;

  constructor(router: Router, private session: SessionService) {
    this.onDocs = this.isDocsUrl(location.pathname);
    this.apply();
    router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        this.onDocs = this.isDocsUrl(e.urlAfterRedirects);
        this.apply();
      });

    this.systemQuery.addEventListener('change', e => {
      this.systemDark.set(e.matches);
      this.apply();
    });

    // Synced once per signed-in user, not on every user$ emission: a late
    // PATCH reply re-emits the user and must not undo a newer toggle.
    session.user$
      .pipe(distinctUntilChanged((a, b) => a?.id === b?.id))
      .subscribe(user => {
        if (!user) return;
        const saved = this.parse(user.preferences?.[PREFERENCE_KEY]) ?? 'system';
        this._theme.set(saved);
        this.writeStored(saved);
        this.apply();
      });
  }

  /** Flips to the opposite of what is showing, as an explicit choice. */
  toggle(): void {
    this.set(this.isDark() ? 'light' : 'dark');
  }

  set(theme: AppTheme): void {
    this._theme.set(theme);
    this.writeStored(theme);
    this.apply();
    if (this.session.user) {
      // Not waited on: the theme is already applied and cached locally; a
      // failed save only means another device won't pick it up.
      this.session.updatePreferences({ [PREFERENCE_KEY]: theme }).subscribe({ error: () => {} });
    }
  }

  private apply(): void {
    const root = document.documentElement;
    if (this.isDark() && !this.onDocs) {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
  }

  private isDocsUrl(url: string): boolean {
    return url === '/docs' || url.startsWith('/docs/') || url.startsWith('/docs?') || url.startsWith('/docs#');
  }

  private parse(value: unknown): AppTheme | null {
    return value === 'light' || value === 'dark' || value === 'system' ? value : null;
  }

  private readStored(): AppTheme | null {
    try {
      return this.parse(localStorage.getItem(STORAGE_KEY));
    } catch {
      return null;
    }
  }

  private writeStored(theme: AppTheme): void {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Private mode / blocked storage: the choice just lasts this session.
    }
  }
}
